// iOS Universal Links: when a build carrying the Associated
// Domains entitlement is installed, https links to these hosts open the app
// and arrive through Capacitor's `appUrlOpen` / `getLaunchUrl` — the same
// channel as the custom-scheme auth links, but with an https URL.
//
// This filter must stay in sync with the server's association file
// (src/server/api/appleAppSiteAssociation.ts): the AASA decides whether iOS
// opens the app at all; this decides where the app navigates. Both
// deliberately scope to round pages only.

export const UNIVERSAL_LINK_HOSTS = [
  "s-process.app",
  "staging.s-process.app",
] as const;

/**
 * The in-app path (path + query + hash) for a universal-link URL, or null
 * for anything that isn't an https round link on one of our domains. The
 * hash rides along so passage deep links land and highlight exactly like
 * they do in a browser.
 */
export function parseUniversalLinkPath(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") {
    return null;
  }
  if (!(UNIVERSAL_LINK_HOSTS as readonly string[]).includes(parsed.hostname)) {
    return null;
  }
  if (parsed.pathname !== "/round" && !parsed.pathname.startsWith("/round/")) {
    return null;
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Marks a URL handled and reports whether the SAME URL was already handled
 * within the window. Capacitor can deliver one cold-start link through BOTH
 * `getLaunchUrl` and `appUrlOpen` (either order), and navigating twice
 * stacks a duplicate history entry and re-fires the landing events. A
 * persistent consumed-flag on both paths would be wrong the other way: a
 * user who later re-taps the same link must navigate again — so the dedupe
 * is a short window, not a permanent mark.
 */
export function createRecentUrlDeduper(
  windowMs = 3000,
): (url: string, now: number) => boolean {
  let last: { url: string; at: number } | null = null;
  return (url, now) => {
    if (last != null && last.url === url && now - last.at < windowMs) {
      return true;
    }
    last = { url, at: now };
    return false;
  };
}

/**
 * SPA-navigates the already-running app to `path`, reproducing the
 * navigation events in-page consumers rely on. A scripted `pushState` alone
 * is not a browser navigation: wouter needs its patched-pushState/popstate
 * signal, and the passage deep-link engine (plus the other fragment
 * consumers) listens for `hashchange`, which `pushState` NEVER fires — so a
 * link carrying a `#passage=` anchor would update the URL without landing
 * whenever the target page is already mounted (same-round cold starts after
 * the boot redirect, and warm opens).
 */
export function navigateInApp(
  path: string,
  { replace = false }: { replace?: boolean } = {},
): void {
  const oldUrl = window.location.href;
  // wouter patches history.pushState/replaceState at module load, so these
  // calls emit its navigation events — deliberately NO synthetic popstate:
  // popstate means "the user traversed history", and both the funder
  // workspace scroll restorer and the dialog close-on-back machinery act on
  // it (a fake one would restore a stale scroll offset as if this were
  // Back, or close an open dialog). `replace` is for cold starts: the boot
  // redirect already wrote the remembered-round entry, and pushing on top
  // of it would put a round the user never chose this session under Back.
  if (replace) {
    window.history.replaceState(null, "", path);
  } else {
    window.history.pushState(null, "", path);
  }
  const newUrl = window.location.href;
  const oldHash = oldUrl.includes("#") ? oldUrl.slice(oldUrl.indexOf("#")) : "";
  const newHash = newUrl.includes("#") ? newUrl.slice(newUrl.indexOf("#")) : "";
  // Deliberately WIDER than same-document browser semantics (which fire only
  // when the fragment itself changes): a link to a DIFFERENT path with the
  // same fragment would, in a real browser, be a full page load whose mount
  // re-runs landing — but this SPA navigation remounts nothing, so the event
  // is the substitute for that mount. Firing it re-lands the anchor the
  // tapped link explicitly targets. An entirely unchanged URL stays silent.
  if (oldUrl !== newUrl && (oldHash !== "" || newHash !== "")) {
    window.dispatchEvent(
      new HashChangeEvent("hashchange", { oldURL: oldUrl, newURL: newUrl }),
    );
  }
}
